import { CheckCircle2, Loader, Circle, Plus, Eye, Download } from 'lucide-react';
import { usePrototype1, ScreenId } from '../Prototype1Context';
import { SectionStatus } from '../prototype1Data';

interface SectionEntry {
  id: string;
  title: string;
  desc: string;
  target: ScreenId;
}

const SECTIONS: SectionEntry[] = [
  {
    id: 'introduction',
    title: 'Introduction',
    desc: 'Overview of the EOAA audit process and district background.',
    target: '06-section-editor-ai-draft',
  },
  {
    id: 'college-eligibility',
    title: 'EOAA Findings: College Eligibility',
    desc: 'Analysis of college eligibility rates across student demographics.',
    target: '03-equity-gaps-drafting',
  },
  {
    id: 'postsecondary-readiness',
    title: 'EOAA Findings: Postsecondary Readiness',
    desc: 'Evaluation of postsecondary readiness indicators and trends.',
    target: '06-section-editor-ai-draft',
  },
  {
    id: 'stakeholder-perceptions',
    title: 'EOAA Findings: Stakeholder Perceptions',
    desc: 'Survey results and analysis of stakeholder feedback on equity.',
    target: '06-section-editor-ai-draft',
  },
  {
    id: 'student-experience',
    title: 'EOAA Findings: Student Experience',
    desc: 'Student interviews, focus groups, and experience data analysis.',
    target: '06-section-editor-ai-draft',
  },
  {
    id: 'recommendations',
    title: 'Recommendations',
    desc: 'Actionable recommendations for improving equity outcomes.',
    target: '06-section-editor-ai-draft',
  },
  {
    id: 'executive-summary',
    title: 'Executive Summary',
    desc: 'High-level summary of findings and key takeaways for leadership.',
    target: '06-section-editor-ai-draft',
  },
];

const ACTION_BY_STATUS: Record<SectionStatus, string> = {
  complete: 'Edit Section',
  'in-progress': 'Continue Editing',
  'not-started': 'Start Section',
};

const StatusBadge = ({ status }: { status: SectionStatus }) => {
  if (status === 'complete') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-semibold text-white rounded-full bg-[#16a34a]">
        Complete
      </span>
    );
  }
  if (status === 'in-progress') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-semibold text-white rounded-full bg-[#f59e0b]">
        In Progress
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium text-[#737373] rounded-full bg-[#f5f5f5] border border-[#e5e5e5]">
      Not Started
    </span>
  );
};

const StatusIcon = ({ status }: { status: SectionStatus }) => {
  if (status === 'complete') return <CheckCircle2 className="w-4 h-4 text-[#16a34a] shrink-0" />;
  if (status === 'in-progress') return <Loader className="w-4 h-4 text-[#f59e0b] animate-spin shrink-0" />;
  return <Circle className="w-4 h-4 text-[#d4d4d4] shrink-0" />;
};

const Screen05DashboardReportOverview = () => {
  const { goToScreen, setActiveSectionId, sectionStatuses, allSectionsComplete } = usePrototype1();
  const completedCount = SECTIONS.filter((s) => sectionStatuses[s.id] === 'complete').length;
  const percent = Math.round((completedCount / SECTIONS.length) * 100);

  return (
    <div className="flex h-full w-full bg-[#fafafa] font-['Inter']">
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex items-center justify-between px-6 h-14 border-b border-[#e5e5e5] shrink-0">
          <span className="text-sm font-medium text-[#0a0a0a]">Dashboard</span>
          <button
            type="button"
            className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-white bg-[#171717] hover:bg-[#2a2a2a] rounded-md transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Report
          </button>
        </div>

        <div className="flex flex-col gap-6 p-6 overflow-y-auto flex-1">
          <div className="flex flex-col gap-4 p-6 rounded-lg bg-[#fafafa] border border-[#e5e5e5] shadow-[0_1px_1.75px_rgba(0,0,0,0.05)]">
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-col gap-2 flex-1">
                <span className="text-lg font-semibold text-[#0a0a0a]">
                  Issaquah School District — Class of 2025 EOAA Report
                </span>
                <span className="text-sm text-[#737373]">
                  {completedCount} of {SECTIONS.length} sections complete
                </span>
              </div>
              <span
                className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold text-white rounded-full shrink-0 ${
                  allSectionsComplete ? 'bg-[#16a34a]' : 'bg-[#f59e0b]'
                }`}
              >
                {allSectionsComplete ? 'Complete' : 'In Progress'}
              </span>
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="w-full h-2 rounded-full bg-[#e5e5e5] overflow-hidden">
                <div className="h-full rounded-full bg-[#171717]" style={{ width: `${percent}%` }} />
              </div>
              <span className="text-xs text-[#737373]">{percent}% complete</span>
            </div>
          </div>

          <span className="text-base font-semibold text-[#0a0a0a]">Report Sections</span>
          <div className="flex flex-col gap-3">
            {SECTIONS.map((s) => {
              const status = sectionStatuses[s.id] ?? 'not-started';
              return (
                <div
                  key={s.id}
                  className="flex flex-col gap-3 p-5 rounded-lg bg-[#fafafa] border border-[#e5e5e5] shadow-[0_1px_1.75px_rgba(0,0,0,0.05)]"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <StatusIcon status={status} />
                      <span className="text-sm font-semibold text-[#0a0a0a]">{s.title}</span>
                    </div>
                    <StatusBadge status={status} />
                  </div>
                  <p className="text-[13px] text-[#737373] leading-[1.5]">{s.desc}</p>
                  <div>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveSectionId(s.id);
                        goToScreen(s.target);
                      }}
                      className={`px-3 py-1.5 text-[13px] font-medium rounded-md border transition-colors ${
                        status === 'in-progress'
                          ? 'bg-[#171717] text-white border-[#171717] hover:bg-[#2a2a2a]'
                          : 'bg-white text-[#0a0a0a] border-[#e5e5e5] hover:bg-gray-50'
                      }`}
                    >
                      {ACTION_BY_STATUS[status]}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {allSectionsComplete && (
            <div className="flex items-center justify-between gap-4 p-5 rounded-lg bg-[#f0fdf4] border border-[#16a34a]/30">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-[#16a34a] shrink-0" />
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold text-[#0a0a0a]">
                    Your EOAA report is complete and ready for export.
                  </span>
                  <span className="text-[13px] text-[#737373]">
                    All {SECTIONS.length} sections have been drafted, reviewed, and approved.
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => goToScreen('08-full-report-review')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-[#0a0a0a] border border-[#e5e5e5] rounded-md bg-white hover:bg-gray-50 transition-colors"
                >
                  <Eye className="w-3.5 h-3.5" />
                  Review Full Report
                </button>
                <button
                  type="button"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-white bg-[#171717] hover:bg-[#2a2a2a] rounded-md transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export Report
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Screen05DashboardReportOverview;
